import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import { useLocale } from '../../utils/LocaleContext';
import { useTranslation } from 'react-i18next';
import { productLabel } from '../../utils/productCatalog';
import { asObjectId } from '../../utils/references';
import { useDebouncedValue, useRequestGate } from '../../hooks/useLatestRequest';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// const MOCK_OFFERS = [
//   { id: 'QT-2026-0089', client: 'Dr. Peter Bachmann',      product: 'Professional Liability', premiumCalc: 7800,  status: 'sent',     created: '2026-02-20', expires: '2026-03-20', broker: 'Helvetica Partners' },
//   { id: 'QT-2026-0088', client: 'Gruppenpraxis Zurich AG', product: 'Daily Sickness',         premiumCalc: 52000, status: 'draft',    created: '2026-02-19', expires: '2026-03-19', broker: 'Assura Broker AG' },
//   { id: 'QT-2026-0087', client: 'Dr. Marie Dubois',        product: 'Legal Protection',       premiumCalc: 3200,  status: 'accepted', created: '2026-02-15', expires: '2026-03-15', broker: 'Fiduciaire Romande SA' },
//   { id: 'QT-2026-0086', client: 'Cabinet Valais SA',       product: 'Professional Liability', premiumCalc: 18500, status: 'declined', created: '2026-02-10', expires: '2026-03-10', broker: 'Ticino Broker SA' },
//   { id: 'QT-2026-0085', client: 'Dr. Klaus Richter',       product: 'Daily Sickness',         premiumCalc: 9800,  status: 'sent',     created: '2026-02-08', expires: '2026-03-08', broker: 'Helvetica Partners' },
//   { id: 'QT-2026-0084', client: 'Praxis am Rhein GmbH',   product: 'Legal Protection',       premiumCalc: 2900,  status: 'accepted', created: '2026-02-05', expires: '2026-03-05', broker: 'Assura Broker AG' },
//   { id: 'QT-2026-0083', client: 'Dr. Isabelle Laurent',   product: 'Professional Liability', premiumCalc: 11200, status: 'expired',  created: '2026-01-20', expires: '2026-02-20', broker: 'Fiduciaire Romande SA' },
// ];

// Product labels come from the i18n bundles via productCatalog rather
// than a hardcoded English map. The old map rendered "Daily Benefit for
// Doctors (PGMA)" in the Product column even with the interface set to
// French, because it never went through t().

// Spreads the raw offer (broker ObjectId, params, convertedTo, clientId,
// lineOfBusiness, currency, coverageDetails) then shadows a few fields
// with display-friendly values for the table. `brokerId` preserves the
// ObjectId so the conversion flow can send it as broker on the policy
// POST without a second fetch. `broker` becomes the display name — the
// backend list endpoint populates broker → {_id, name} and also flattens
// brokerName, so we accept either shape and fall back to a dash rather
// than leaking the raw ObjectId (Test Run 7-24: table showed the OID).
export const toDisplay = (o, t) => {
  const brokerId = asObjectId(o.broker) || undefined;
  const brokerName = o.brokerName
    || (o.broker && typeof o.broker === 'object' ? o.broker.name : null);
  return {
    ...o,
    id: o.offerNumber || o._id || o.id,
    _id: o._id,
    brokerId,
    brokerName,
    // DATA, not display text: '' when absent. This object seeds the revision
    // and edit-draft forms (reviseSeed) and offer → policy conversion, so a
    // display dash here was posted as `broker: "—"` (CH, 2026-09-28). Table
    // and OfferDetailPanel render the dash themselves.
    broker: brokerName || '',
    // The server resolves the client identity it wants displayed: the
    // stored label when it is a real name, otherwise whatever the
    // policyholder block still holds, otherwise a dash. Never a legal form
    // and never invented — see backend services/offerIdentity.js.
    client: o.clientDisplay || o.client || '',
    handlerName: o.handlerName || o.createdByName || null,
    preparedBy: o.preparedBy || null,
    variant: o.variant || 1,
    product: productLabel(t, o.lineOfBusiness, null, 'long') || o.product || '',
    premiumCalc: o.premiumCalc || 0,
    created: o.createdAt ? o.createdAt.slice(0, 10) : o.created || '',
    expires: o.expiresAt ? o.expiresAt.slice(0, 10) : o.expires || '',
  };
};

const useOffersFunctions = () => {
  useLocale();
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Product, broker and created-date range. offerListFilter() on the backend
  // has accepted all four since the batch endpoint was written — its own
  // comment says "parameters the LIST screen does not currently offer" — but
  // the list never sent them. They became load-bearing when the export cap
  // dropped to 50: "narrow the filter" is only actionable advice if there are
  // filters narrow enough to reach 50. "all" is the absent value for the two
  // dropdowns, '' for the two dates.
  const [productFilter, setProductFilter] = useState('all');
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // X-02: page size controllable via PageSizeSelector.
  const [PAGE_SIZE, setPageSize] = useState(20);

  const STATUS_OPTIONS = ['all', 'draft', 'sent', 'accepted', 'declined', 'expired'];

  // ONE description of the current filter, used for both the list query and
  // the batch export body. They have to be the same object or the count in
  // the export dialog and the contents of the ZIP drift apart — which is the
  // exact failure the batch endpoint reusing offerListFilter() prevents on
  // the server side, undone here by building the query twice.
  // The box updates on every keystroke; the QUERY only once typing pauses.
  // See hooks/useLatestRequest.js for the out-of-order race this closes.
  const debouncedSearch = useDebouncedValue(search, 300);
  const beginRequest = useRequestGate();

  const activeFilters = useMemo(() => {
    const f = {};
    if (statusFilter !== 'all') f.status = statusFilter;
    if (debouncedSearch) f.search = debouncedSearch;
    if (productFilter !== 'all') f.product = productFilter;
    if (brokerFilter !== 'all') f.broker = brokerFilter;
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    return f;
  }, [statusFilter, debouncedSearch, productFilter, brokerFilter, dateFrom, dateTo]);

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  const clearFilters = useCallback(() => {
    setStatusFilter('all');
    setSearch('');
    setProductFilter('all');
    setBrokerFilter('all');
    setDateFrom('');
    setDateTo('');
  }, []);
  const STATUS_LABELS = { all: 'All Status', draft: 'Draft', sent: 'Issued', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };

  const fetchOffers = useCallback(async () => {
    const req = beginRequest();
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      Object.entries(activeFilters).forEach(([k, v]) => params.set(k, v));
      const resp = await axios.get(`${BASE_URL}/offers?${params}`, { withCredentials: true, signal: req.signal });
      // A newer query has been sent since this one: its answer is the one the
      // user is waiting for, not this one.
      if (!req.isCurrent()) return;
      // Always sync — previous version only replaced the list when
      // resp.data.data.length > 0, so a filter that narrowed the list
      // to zero rows quietly kept the prior page's rows on screen
      // (visible when Anjani toggled status filters on the offers page).
      const rows = Array.isArray(resp.data?.data) ? resp.data.data : [];
      setOffers(rows.map(o => toDisplay(o, t)));
      setTotal(Number(resp.data?.total) || rows.length);
    } catch {
      /* empty state on error */
    } finally {
      // Only the latest request owns the spinner; a superseded one finishing
      // must not flip it off while the current one is still loading.
      if (req.isCurrent()) setLoading(false);
    }
  }, [page, activeFilters, PAGE_SIZE, t, beginRequest]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // D-11: deep-link from Client Detail's Offers tab hits /offers?open=<id>.
  // Fetch that specific offer directly (it may not be on page 1) and open
  // the detail panel. Clear the query param after opening so a refresh
  // doesn't re-open it. Runs once per pathname+search change.
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('open');
    if (!openId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/offers/${openId}`, { withCredentials: true });
        const raw = resp.data?.data?.data || resp.data?.data || null;
        if (!cancelled && raw) setSelectedOffer(toDisplay(raw, t));
      } catch {
        /* offer not found or access denied — leave panel closed */
      } finally {
        if (!cancelled) history.replace(location.pathname);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, location.search, history, t]);

  const handleCloseNew = useCallback(() => setShowNew(false), []);
  const handleCreated = useCallback(() => {
    fetchOffers();
    setShowNew(false);
  }, [fetchOffers]);

  // D-15: pagination was double-computed. Backend already returns 20
  // per page + a `total` header, but this hook then re-filtered and
  // re-sliced client-side using filtered.length, producing
  // totalPages=1 regardless of the real total (Anjani saw 60 offers
  // in the API response but only 20 in the UI, no next-page control).
  //
  // Now: server is authoritative for pagination — offers IS the
  // current page. `filtered` still runs so the front-end search+
  // status filter stays instant on the loaded page (backend also
  // honours ?search and ?status, so cross-page filtering triggers
  // a refetch on next page click). totalPages uses `total` from the
  // server, matching what the API actually returned.
  const filtered = offers.filter(o => {
    const q = search.toLowerCase();
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (q && !o.id.toLowerCase().includes(q) && !o.client.toLowerCase().includes(q) && !o.product.toLowerCase().includes(q)) return false;
    return true;
  });

  const paginated = filtered;
  const totalPages = Math.max(1, Math.ceil((total || filtered.length) / PAGE_SIZE));

  const accepted = offers.filter(o => o.status === 'accepted').length;
  const convRate = offers.length > 0 ? Math.round((accepted / offers.length) * 100) : 0;

  return {
    offers,
    total,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    productFilter,
    setProductFilter,
    brokerFilter,
    setBrokerFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    activeFilters,
    hasActiveFilters,
    clearFilters,
    showNew,
    setShowNew,
    selectedOffer,
    setSelectedOffer,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    setPageSize: (n) => { setPageSize(n); setPage(1); },
    loading,
    STATUS_OPTIONS,
    STATUS_LABELS,
    fetchOffers,
    handleCloseNew,
    handleCreated,
    filtered,
    paginated,
    totalPages,
    convRate,
  };
};

export default useOffersFunctions;
